import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { LuArrowLeft, LuCreditCard, LuDownload } from 'react-icons/lu';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorAlert from '../../components/ErrorAlert';
import { invoicesAPI, customerAPI } from '../../services/api';
import api from '../../services/api';
import ActivityTimeline from '../../components/ActivityTimeline';

const STATUS_BADGES = {
  pending: 'bg-yellow-100 text-yellow-800',
  sent: 'bg-blue-100 text-blue-800',
  paid: 'bg-green-100 text-green-800',
  overdue: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-800'
};

const FEE_TYPE_LABELS = {
  storage_fee: 'Storage',
  handling_inbound: 'Inbound Handling',
  handling_outbound: 'Outbound Handling',
  receiving_pallet: 'Receiving - Pallet',
  receiving_carton: 'Receiving - Carton',
  serial_capture: 'Serial Number Capture',
  mac_capture: 'MAC Address Capture',
  relabel: 'Relabel Service',
  order_picking: 'Order Picking',
  picking_fee: 'Picking',
  packing_fee: 'Packing',
  shipping_fee: 'Shipping',
  asset_tagging: 'Asset Tagging',
  reboxing: 'Reboxing',
  kitting_fee: 'Kitting',
  assembly_fee: 'Assembly',
  returns_handling: 'Returns Handling',
  labour: 'Labour',
  insurance: 'Insurance'
};

const openPdfBlob = (blob) => {
  const blobUrl = window.URL.createObjectURL(blob);
  window.open(blobUrl, '_blank', 'noopener,noreferrer');
  window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
};

const InvoiceDetail = () => {
  const { id } = useParams();
  const [payLoading, setPayLoading] = useState(false);
  const [payError, setPayError] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const { data: invoice, isLoading, isError } = useQuery(
    ['invoice', id],
    () => invoicesAPI.getInvoice(id),
    { enabled: !!id }
  );

  const handlePayInvoice = async () => {
    setPayLoading(true);
    setPayError(null);
    try {
      const response = await api.get(`/billing/invoices/${id}/payment-link`);
      const result = response.data;
      if (result?.success && result?.paymentLink) {
        window.open(result.paymentLink, '_blank', 'noopener,noreferrer');
      } else {
        setPayError('Contact us for payment options.');
      }
    } catch {
      setPayError('Contact us for payment options.');
    } finally {
      setPayLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const response = await invoicesAPI.downloadInvoicePdf(id);
      openPdfBlob(response.data);
    } catch {
      setPayError('Failed to open invoice PDF. Please try again later.');
    } finally {
      setPdfLoading(false);
    }
  };

  if (isLoading) return <LoadingSpinner />;
  if (isError || !invoice) return <ErrorAlert message="Error loading invoice. Please try again later." />;

  const items = invoice.items || [];
  const lineItems = items.filter(i => i.fee_type !== 'tax');
  const taxItem = items.find(i => i.fee_type === 'tax');
  const subtotal = lineItems.reduce((sum, i) => sum + (parseFloat(i.amount || i.rate * i.quantity) || 0), 0);

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A';

  const canPay = ['pending', 'sent', 'overdue'].includes(invoice.status);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-4xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <Link
          to="/customer/invoices"
          className="inline-flex items-center text-sm font-medium text-indigo-600 hover:text-indigo-500"
        >
          <LuArrowLeft className="mr-1 h-4 w-4" />
          Back to Invoices
        </Link>
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={handleDownloadPdf}
            disabled={pdfLoading}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <LuDownload className="h-4 w-4 mr-1.5" />
            {pdfLoading ? 'Opening PDF...' : 'Download PDF'}
          </button>
          {canPay && (
            <button
              type="button"
              onClick={handlePayInvoice}
              disabled={payLoading}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              <LuCreditCard className="h-4 w-4 mr-1.5" />
              {payLoading ? 'Loading...' : 'Pay Invoice'}
            </button>
          )}
        </div>
      </div>

      {payError && (
        <div className="mb-4 rounded-md bg-yellow-50 p-3">
          <p className="text-sm text-yellow-700">{payError}</p>
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-semibold text-gray-900">Invoice {invoice.invoiceNumber}</h1>
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_BADGES[invoice.status] || STATUS_BADGES.pending}`}>
              {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
            </span>
          </div>
        </div>

        {/* Details grid */}
        <div className="px-6 py-4 grid grid-cols-2 sm:grid-cols-4 gap-4 border-b border-gray-200 text-sm">
          <div>
            <div className="text-gray-500">Issue Date</div>
            <div className="font-medium">{formatDate(invoice.issueDate)}</div>
          </div>
          <div>
            <div className="text-gray-500">Due Date</div>
            <div className="font-medium">{formatDate(invoice.dueDate)}</div>
          </div>
          <div>
            <div className="text-gray-500">Paid Date</div>
            <div className="font-medium">{invoice.paidDate ? formatDate(invoice.paidDate) : '-'}</div>
          </div>
          <div>
            <div className="text-gray-500">Total</div>
            <div className="text-lg font-bold text-gray-900">${parseFloat(invoice.totalAmount || 0).toFixed(2)}</div>
          </div>
        </div>

        {/* Line Items */}
        <div className="px-6 py-4">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left text-xs font-medium text-gray-500 uppercase py-2">Description</th>
                <th className="text-right text-xs font-medium text-gray-500 uppercase py-2 w-20">Qty</th>
                <th className="text-right text-xs font-medium text-gray-500 uppercase py-2 w-24">Rate</th>
                <th className="text-right text-xs font-medium text-gray-500 uppercase py-2 w-24">Amount</th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((item, index) => (
                <tr key={index} className="border-b border-gray-100">
                  <td className="py-2 text-sm text-gray-900">
                    <div>{item.description || item.name || 'Service'}</div>
                    {item.fee_type && (
                      <div className="text-xs text-gray-400">
                        {FEE_TYPE_LABELS[item.fee_type] || item.fee_type.replace(/_/g, ' ')}
                      </div>
                    )}
                  </td>
                  <td className="py-2 text-sm text-gray-600 text-right">{item.quantity || 1}</td>
                  <td className="py-2 text-sm text-gray-600 text-right">${parseFloat(item.rate || item.unit_price || 0).toFixed(2)}</td>
                  <td className="py-2 text-sm text-gray-900 text-right">${parseFloat(item.amount || (item.rate * item.quantity) || 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-gray-200">
                <td colSpan="3" className="py-2 text-sm font-medium text-gray-600 text-right">Subtotal</td>
                <td className="py-2 text-sm font-medium text-gray-900 text-right">${subtotal.toFixed(2)}</td>
              </tr>
              {taxItem && (
                <tr>
                  <td colSpan="3" className="py-1 text-sm text-gray-500 text-right">{taxItem.description || 'Tax'}</td>
                  <td className="py-1 text-sm text-gray-700 text-right">${parseFloat(taxItem.amount || 0).toFixed(2)}</td>
                </tr>
              )}
              <tr className="border-t border-gray-300">
                <td colSpan="3" className="py-2 text-sm font-bold text-gray-900 text-right">Total</td>
                <td className="py-2 text-sm font-bold text-gray-900 text-right">${parseFloat(invoice.totalAmount || 0).toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {lineItems.some((item) => ['serial_capture', 'mac_capture', 'relabel'].includes(item.fee_type)) && (
          <div className="px-6 py-4 border-t border-amber-200 bg-amber-50">
            <p className="text-sm text-amber-800">
              This invoice includes one-time inbound receipt services requested on the related ASN or manifest.
            </p>
          </div>
        )}

        {/* Notes */}
        {invoice.notes && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="text-xs font-medium text-gray-500 uppercase mb-1">Notes</div>
            <p className="text-sm text-gray-700">{invoice.notes}</p>
          </div>
        )}
      </div>

      {/* Recent Activity */}
      <div className="mt-6 bg-white shadow rounded-lg p-6">
        <ActivityTimeline
          fetchFn={(params) => customerAPI.getEntityTimeline('Invoice', id, params)}
          entityType="Invoice"
          entityId={id}
          limit={25}
          title="Recent Activity"
        />
      </div>
    </div>
  );
};

export default InvoiceDetail;
